import React, { useState, useRef, useEffect, useCallback, Component, ErrorInfo } from 'react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X, Mail } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import AIChatTab from './AIChatTab';
import EmailChatTab from './EmailChatTab';

// Error boundary to catch React DOM issues
class ChatErrorBoundary extends Component<{ children: React.ReactNode, onError: () => void }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode, onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Chat component error:", error, errorInfo);
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      // Reset error state after a short delay
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 500);
      
      return null; // Render nothing when an error occurs
    }

    return this.props.children;
  }
}

type ChatMode = 'info' | 'email';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  files?: File[];
}

interface Props {
  externalOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

// Simple helper to check for leftover overlays
const checkForOverlays = () => {
  const overlays = document.querySelectorAll('.backdrop-overlay, .hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
  if (overlays.length > 0) {
    console.log('WARNING: Found leftover overlays in DOM after chat closed:', overlays);
    
    // Force remove any leftover overlays
    overlays.forEach(el => {
      console.log('Force removing:', el);
      el.parentNode?.removeChild(el);
    });
    
    // Double-check after removal
    setTimeout(() => {
      const remainingOverlays = document.querySelectorAll('.backdrop-overlay, .hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
      if (remainingOverlays.length > 0) {
        console.log('CRITICAL: Still found overlays after forced removal:', remainingOverlays);
      } else {
        console.log('All overlays successfully removed');
      }
    }, 50);
  } else {
    console.log('No leftover overlays found in DOM - good!');
  }
};

// Function to handle a hard reset if needed
const forceCleanup = () => {
  document.body.style.overflow = '';
  
  // Force remove all chat-related elements
  const elementsToRemove = document.querySelectorAll('.backdrop-overlay, .hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
  elementsToRemove.forEach(el => {
    try {
      el.parentNode?.removeChild(el);
    } catch (e) {
      console.error("Error removing element:", e);
    }
  });
  
  console.log("Forced cleanup complete");
};

// Enhanced force cleanup function at module level
function forceRemoveAllChatElements() {
  document.body.style.overflow = '';
  
  // Force remove all chat-related elements
  const elementsToRemove = document.querySelectorAll('.backdrop-overlay, .hologram-chat, .hologram-beam, .hud-interface, .digital-energy');
  elementsToRemove.forEach(el => {
    try {
      console.log('Removing element:', el);
      el.parentNode?.removeChild(el);
    } catch (e) {
      console.error("Error removing element:", e);
    }
  });
  
  // Make sure body scroll is enabled
  document.body.style.overflow = '';
}

// Call force cleanup on module load to clean any leftover elements
forceRemoveAllChatElements();

const AIChat: React.FC<Props> = ({ externalOpen, onOpenChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  
  // State declarations
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<ChatMode>('info');
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const [errorOccurred, setErrorOccurred] = useState(false);
  
  // Additional refs
  const notificationSound = useRef<HTMLAudioElement | null>(null);
  const chatComponentsRef = useRef<HTMLDivElement>(null);
  
  // Error handler function
  const handleError = useCallback(() => {
    console.log("Error detected, performing cleanup");
    setErrorOccurred(true);
    setIsOpen(false);
    document.body.style.overflow = '';
    
    // Force cleanup DOM elements
    forceCleanup();
    
    // Reset error state after a delay
    setTimeout(() => {
      setErrorOccurred(false);
    }, 1000);
  }, []);
  
  // Log isOpen state changes for debugging
  useEffect(() => {
    console.log("isOpen changed to:", isOpen);
    
    // When isOpen changes to false, schedule a check for leftover overlays
    if (isOpen === false) {
      // Wait for AnimatePresence exit animations to complete (they take about 0.5s)
      setTimeout(() => {
        checkForOverlays();
      }, 1000);
    }
  }, [isOpen]);
  
  // Function to find the profile image and position the hologram
  const positionPanel = useCallback(() => {
    try {
      // Always set a safe default position first - this ensures the chat is visible even if profile detection fails
      const defaultY = Math.max(100, Math.min(window.innerHeight - 400, window.innerHeight / 2 - 200));
      const defaultX = Math.max(10, Math.min(window.innerWidth - 410, window.innerWidth / 2 - 150));
      
      setPanelPosition({
        top: defaultY,
        left: defaultX
      });
      
      console.log("Default position set:", defaultY, defaultX);
      
      // First, check if we're on the homepage
      const profileOnHomepage = document.querySelector('.hologram-container img');
      // Or on the about page
      const profileOnAboutPage = document.querySelector('.about-profile img');
      
      let profileElement = profileOnHomepage || profileOnAboutPage;
      
      if (profileElement) {
        console.log("Profile element found");
        const rect = profileElement.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Check if the profile image is in the viewport or close to it
        const isInView = (
          rect.top >= -rect.height &&
          rect.left >= -rect.width &&
          rect.bottom <= (window.innerHeight + rect.height) &&
          rect.right <= (window.innerWidth + rect.width)
        );
        
        // Only use the profile-based positioning if it's visible in viewport
        if (isInView) {
          console.log("Profile is in view, positioning chat next to profile");
          
          // Calculate positions relative to the profile image
          // Use window-relative positions (fixed positioning)
          const chatXPosition = windowWidth - centerX > 350 
            ? centerX + rect.width/2 + 20 // Position to the right if there's room
            : centerX - 300 - 20; // Position to the left if not
          
          // Update position state - position it at the same level as the profile image
          setPanelPosition({
            top: Math.max(20, centerY - 200), // Position the chat higher up, with min offset from top of screen
            left: Math.max(10, Math.min(windowWidth - 310, chatXPosition)) // Ensure chat is not off-screen
          });

          // Set the decorative elements only after we're sure positioning worked
          setTimeout(() => {
            try {
              // Safely position HUD elements only if they exist
              const hudElement = document.querySelector('.hud-interface') as HTMLElement | null;
              if (hudElement) {
                hudElement.style.top = `${centerY - 60}px`;
                hudElement.style.left = `${centerX - 40}px`;
                hudElement.style.opacity = '1';
              }

              // Safely position beam element only if it exists
              const beamElement = document.querySelector('.hologram-beam') as HTMLElement | null;
              if (beamElement) {
                beamElement.style.top = `${centerY - 40}px`;
                beamElement.style.left = `${centerX}px`;
                beamElement.style.height = `${40}px`;
                beamElement.style.transform = windowWidth - centerX > 350 
                  ? 'rotate(30deg) scaleY(1)' 
                  : 'rotate(-30deg) scaleY(1)';
                beamElement.style.opacity = '0.7';
              }
              
              // Safely position energy element only if it exists
              const energyElement = document.querySelector('.digital-energy') as HTMLElement | null;
              if (energyElement) {
                const size = Math.max(rect.width, rect.height) * 1.5; // 50% larger than profile
                energyElement.style.width = `${size}px`;
                energyElement.style.height = `${size}px`;
                energyElement.style.top = `${centerY - size/2}px`;
                energyElement.style.left = `${centerX - size/2}px`;
                energyElement.style.opacity = '0.8';
              }
            } catch (err) {
              console.error("Error positioning decorative elements:", err);
            }
          }, 300);
        } else {
          console.log("Profile not in view, using default position");
          // No decorative elements in light mode
        }
      } else {
        console.log("No profile found, using default position");
      }
      
      return true;
    } catch (error) {
      console.error("Error in positionHologram:", error);
      // Use a safe default position if an error occurs
      setPanelPosition({
        top: Math.min(window.innerHeight / 2 - 200, Math.max(20, 100)),
        left: Math.min(window.innerWidth / 2 - 150, Math.max(10, window.innerWidth - 310))
      });
      return false;
    }
  }, []);
  
  // Helper function to hide decorative elements
  const hideDecorativeElements = useCallback(() => {}, []);

  // Initial positioning when chat opens
  useEffect(() => {
    if (isOpen && !errorOccurred) {
      const timeoutId = setTimeout(() => {
        try {
          positionPanel();
        } catch (e) {
          console.error("Error positioning hologram:", e);
          handleError();
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, positionPanel, errorOccurred, handleError]);
  
  // Reposition on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && !errorOccurred) {
        try {
          positionPanel();
        } catch (e) {
          console.error("Error in resize handler:", e);
          handleError();
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, positionPanel, errorOccurred, handleError]);

  // Initialize notification sound and welcome message
  useEffect(() => {
    notificationSound.current = new Audio('/sounds/notification.mp3');
    
    // Set initial welcome message if no messages exist
    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: t('chat.welcomeMessage')
        }
      ]);
    }
  }, [t, messages.length]);

  // Handle external open/close requests
  useEffect(() => {
    if (externalOpen !== undefined && !errorOccurred) {
      if (externalOpen) {
        openChat();
      } else {
        handleClose();
      }
    }
  }, [externalOpen, errorOccurred]);

  // Notify when open state changes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  // Track unread messages
  useEffect(() => {
    if (!isOpen && messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      setUnreadCount(prev => prev + 1);
      notificationSound.current?.play().catch(err => console.log('Audio play failed:', err));
    }
  }, [messages, isOpen]);

  // Reset unread count when chat opens
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Enhanced close handler using the global cleanup function
  const handleClose = useCallback(() => {
    console.log("handleClose called");
    
    // First set state
    setIsOpen(false);
    onOpenChange?.(false);
    setMode('info');
    
    // Use our enhanced cleanup function
    forceRemoveAllChatElements();
    
    // Double check after a small delay
    setTimeout(() => {
      forceRemoveAllChatElements();
      console.log("Scrolling restored: overflow =", document.body.style.overflow);
    }, 100);
  }, [onOpenChange]);

  // Optimized open handler that positions directly at the profile
  const openChat = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Don't open if we're in an error state
    if (errorOccurred) {
      console.log("Not opening chat due to error state");
      return;
    }
    
    // Detect profile position synchronously before opening chat
    // This prevents the jump effect
    let initialPosition = { 
      top: Math.max(100, window.innerHeight / 2 - 250),
      left: Math.max(20, window.innerWidth / 2 - 200)
    };
    
    try {
      // Check for profile image
      const profileElement = document.querySelector('.hologram-container img') || 
                             document.querySelector('.about-profile img');
      
      if (profileElement) {
        const rect = profileElement.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Check if the profile image is in the viewport
        const isInView = (
          rect.top >= -rect.height &&
          rect.left >= -rect.width &&
          rect.bottom <= (window.innerHeight + rect.height) &&
          rect.right <= (window.innerWidth + rect.width)
        );
        
        if (isInView) {
          // Calculate position next to profile
          const chatXPosition = windowWidth - centerX > 350 
            ? centerX + rect.width/2 + 20
            : centerX - 300 - 20;
          
          initialPosition = {
            top: Math.max(20, centerY - 200),
            left: Math.max(10, Math.min(windowWidth - 310, chatXPosition))
          };
          
          console.log("Opening chat next to profile at:", initialPosition.top, initialPosition.left);
        }
      }
    } catch (err) {
      console.warn("Error detecting profile position:", err);
      // Fall back to default center position (already set)
    }
    
    // Set position and open chat
    setPanelPosition(initialPosition);
    document.body.style.overflow = 'hidden';
    setIsOpen(true);
    onOpenChange?.(true);
    
    // No decorative elements in light mode
  }, [onOpenChange, errorOccurred, handleError, hideDecorativeElements]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''; // Ensure scrolling is enabled when component unmounts
      forceCleanup(); // Clean up any DOM elements
    };
  }, []);

  // Add global event listener for opening chat
  useEffect(() => {
    const handleGlobalOpen = () => {
      openChat();
    };
    
    window.addEventListener('openAIChat', handleGlobalOpen);
    
    return () => {
      window.removeEventListener('openAIChat', handleGlobalOpen);
    };
  }, [openChat]);

  // Cleanup useEffect to ensure no leftover elements when component unmounts
  useEffect(() => {
    return () => {
      // Ensure we clean up any stray elements when component unmounts
      document.body.style.overflow = '';
      forceCleanup();
    };
  }, []);

  // If we're in an error state, render a simpler version
  if (errorOccurred) {
    return (
      <button
        onClick={() => {
          setErrorOccurred(false);
          setTimeout(() => {
            openChat();
          }, 500);
        }}
        className="fixed bottom-4 right-4 p-4 bg-white dark:bg-gray-800 border border-slate-300 dark:border-gray-700 rounded-full shadow-md hover:border-slate-400 dark:hover:border-gray-600 transition-colors duration-300 group"
        style={{ zIndex: 9999 }}
        aria-label={t('chat.openChat')}
      >
        <img src="/favicon.svg" alt="Open chat" className="w-6 h-6" />
        {unreadCount > 0 && (
          <div className="absolute -top-2 -right-2 bg-sky-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
            {unreadCount}
          </div>
        )}
      </button>
    );
  }

  return (
    <ChatErrorBoundary onError={handleError}>
      {/* Chat button */}
      <button
        onClick={openChat}
        className="fixed bottom-4 right-4 p-4 bg-white dark:bg-gray-800 border border-slate-300 dark:border-gray-700 rounded-full shadow-md hover:border-slate-400 dark:hover:border-gray-600 transition-colors duration-300 group chat-button"
        style={{
          zIndex: 9999 // Ensure button is always on top
        }}
        tabIndex={0}
        aria-label={t('chat.openChat')}
      >
        <img src="/favicon.svg" alt="Open chat" className="w-6 h-6" />
        {unreadCount > 0 && (
          <div className="absolute -top-2 -right-2 bg-sky-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
            {unreadCount}
          </div>
        )}
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1
          bg-white dark:bg-gray-800 rounded text-sm text-slate-900 dark:text-gray-100 opacity-0 group-hover:opacity-100
          transition-opacity duration-300 whitespace-nowrap border border-slate-200 dark:border-gray-700">
          {t('chat.openChat')}
        </span>
      </button>

      {/* Render chat interface directly without conditional when open */}
      {isOpen && (
        <div ref={chatComponentsRef} id="chat-container" className="chat-components-container">
      {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-overlay"
            style={{ zIndex: 1000 }}
            onClick={handleClose}
            data-testid="chat-backdrop"
          />
          
          {/* Simple chat interface without AnimatePresence - with enforced visibility */}
          <div className="fixed w-full max-w-[480px] bg-white dark:bg-gray-800 backdrop-blur-sm 
            rounded-xl shadow-2xl overflow-hidden flex flex-col border border-slate-200 dark:border-gray-700"
            style={{
              top: `${panelPosition.top}px`,
              left: `${panelPosition.left}px`,
              height: "600px",
              maxHeight: "600px",
              maxWidth: "480px",
              zIndex: 1002,
              opacity: 1,
              transform: 'scale(1)',
              display: 'flex',
              visibility: 'visible'
            }}
            onClick={(e) => e.stopPropagation()}
            ref={(el) => {
              if (el) {
                // Set a timeout to force the element to be visible
                setTimeout(() => {
                  el.style.opacity = '1';
                  el.style.visibility = 'visible';
                  el.style.display = 'flex';
                }, 100);
              }
            }}
          >
            {/* Chat Header */}
            <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              
              <div className="flex-1"></div>

              <div className="flex gap-2">
                {/* Info tab */}
                <button
                  className={`relative px-3 py-1 rounded-md transition-colors ${
                    mode === 'info'
                      ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                      : 'text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMode('info');
                  }}
                >
                  <span className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center">
                      <img src="/favicon.svg" alt="Assistant" className="w-7 h-7" />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-base font-medium text-slate-900 dark:text-gray-100">VL Assistant</span>
                      <span className="text-xs text-slate-500 dark:text-gray-400"><span className="animate-pulse">ONLINE</span></span>
                    </div>
                  </span>
                </button>

                {/* Email tab */}
                <button
                  className={`relative px-3 py-1 rounded-md transition-colors ${
                    mode === 'email'
                      ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                      : 'text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMode('email');
                  }}
                >
                  <span className="flex items-center justify-center">
                    <Mail className="w-6 h-6" />
                  </span>
                </button>

                {/* WhatsApp quick action */}
                <a
                  href="https://wa.me/393806331704"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 rounded-md text-white bg-emerald-600 dark:bg-emerald-700 hover:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
                >
                  WhatsApp
                </a>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors ml-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Content - Different based on mode */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {mode === 'info' ? (
                <AIChatTab 
                  messages={messages} 
                  setMessages={setMessages} 
                />
              ) : (
                <EmailChatTab onClose={handleClose} />
              )}
            </div>
            
            {/* Subtle corner accents */}
            <div className="absolute top-1/4 -left-3 w-3 h-12 border-l-2 border-t-2 border-b-2 border-slate-200 dark:border-gray-700"></div>
            <div className="absolute bottom-1/4 -right-3 w-3 h-12 border-r-2 border-t-2 border-b-2 border-slate-200 dark:border-gray-700"></div>
            <div className="absolute -bottom-3 left-1/4 h-3 w-12 border-l-2 border-r-2 border-b-2 border-slate-200 dark:border-gray-700"></div>
          </div>
        </div>
      )}
    </ChatErrorBoundary>
  );
};

export default AIChat;